import React, { useState, useEffect } from 'react';
import TeamSettings from './TeamSettings';
import { getOrganization, updateOrganization } from '../../services/firestoreService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { isMobileNative } from '../../utils/platform';

// ============================================================
// SETTINGS PAGE — Tabbed shell for Profile / Team / Account / Billing
// ============================================================

// Billing tab is hidden on native (iOS/Android) for App Store compliance.
const ALL_TABS = [
  { id: 'profile', label: 'My Profile' },
  { id: 'team',    label: 'Team' },
  { id: 'account', label: 'Account' },
  { id: 'billing', label: 'Billing' },
];
const TABS = isMobileNative
  ? ALL_TABS.filter(t => t.id !== 'billing')
  : ALL_TABS;

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { maxWidth: 640, margin: '0 auto', padding: '32px 24px' },
  card:    { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 24 },
  section: { fontSize: 13, color: '#89A8B1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, display: 'block' },
  desc:    { fontSize: 14, color: '#516469', lineHeight: 1.6, marginBottom: 0 },
  label:   { fontSize: 12, fontWeight: 700, color: '#071D39', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, display: 'block' },
  input:   { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#071D39', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: '#FFFFFF' },
  saveBtn: { padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none', cursor: 'pointer' },
};

// ============================================================
// MY PROFILE TAB
// ============================================================
const ProfileTab = ({ member, isAdmin, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: member?.firstName || '',
    lastName:  member?.lastName  || '',
    email:     member?.email     || '',
    phone:     member?.phone     || '',
    role:      member?.role      || 'member',
    notificationPreference: member?.notificationPreference || 'email',
  });
  const [errors, setErrors]   = useState({});
  const [saved,  setSaved]    = useState(false);

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim())  e.lastName  = 'Last name is required';
    if (
      (formData.notificationPreference === 'sms' || formData.notificationPreference === 'both') &&
      !formData.phone.trim()
    ) {
      e.notificationPreference = 'Phone number is required for SMS notifications';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const updates = { ...formData };
    delete updates.role;
    delete updates.email;
    onSave(member.id, updates);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={S.page}>

      {/* Description card */}
      <div style={S.card}>
        <span style={S.section}>My Profile</span>
        <p style={S.desc}>Manage your personal information and notification preferences.</p>
      </div>

      {/* Personal Info card */}
      <div style={S.card}>
        <span style={S.section}>Personal Information</span>

        {saved && (
          <div style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 8, backgroundColor: '#DCFCE7', color: '#16A34A', fontSize: 14, fontWeight: 600 }}>
            Profile saved successfully!
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={S.label}>First Name</label>
          <input
            style={{ ...S.input, borderColor: errors.firstName ? '#DC2626' : '#E2E8F0' }}
            value={formData.firstName}
            onChange={e => setFormData({ ...formData, firstName: e.target.value })}
          />
          {errors.firstName && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4 }}>{errors.firstName}</p>}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={S.label}>Last Name</label>
          <input
            style={{ ...S.input, borderColor: errors.lastName ? '#DC2626' : '#E2E8F0' }}
            value={formData.lastName}
            onChange={e => setFormData({ ...formData, lastName: e.target.value })}
          />
          {errors.lastName && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4 }}>{errors.lastName}</p>}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={S.label}>Email</label>
          <input
            style={{ ...S.input, backgroundColor: '#F7FAFC', color: '#89A8B1' }}
            value={formData.email}
            disabled
          />
          <p style={{ fontSize: 12, color: '#89A8B1', marginTop: 4 }}>Email cannot be changed</p>
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={S.label}>Phone</label>
          <input
            style={S.input}
            type="tel"
            value={formData.phone}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
      </div>

      {/* Role card */}
      <div style={S.card}>
        <span style={S.section}>Role</span>
        <div style={{ ...S.input, backgroundColor: '#F7FAFC', color: '#516469', cursor: 'default' }}>
          {formData.role === 'admin' ? 'Admin' : 'Member'}
        </div>
      </div>

      {/* Notification Preferences card */}
      <div style={S.card}>
        <span style={S.section}>Notification Preferences</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { value: 'email', label: 'Email only' },
            { value: 'sms',   label: 'SMS only' },
            { value: 'both',  label: 'Both (Email + SMS)' },
            { value: 'none',  label: 'None' },
          ].map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#071D39' }}>
              <input
                type="radio"
                name="notificationPreference"
                value={opt.value}
                checked={formData.notificationPreference === opt.value}
                onChange={e => setFormData({ ...formData, notificationPreference: e.target.value })}
                style={{ accentColor: '#75BB2E', width: 16, height: 16 }}
              />
              {opt.label}
            </label>
          ))}
        </div>
        {errors.notificationPreference && (
          <p style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{errors.notificationPreference}</p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button style={S.saveBtn} onClick={handleSubmit}>
          {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// TEAM TAB WRAPPER
// ============================================================
const TeamTab = (props) => (
  <div>
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 24px 0' }}>
      <div style={S.card}>
        <span style={S.section}>Team</span>
        <p style={S.desc}>Manage your team members, roles, and default task assignments for every deal type.</p>
      </div>
    </div>
    <TeamSettings {...props} />
  </div>
);

// ============================================================
// ACCOUNT TAB
// ============================================================
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const AccountTab = ({ orgId }) => {
  const [orgName,     setOrgName]     = useState('');
  const [timezone,    setTimezone]    = useState('America/New_York');
  const [logoUrl,     setLogoUrl]     = useState('');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const org = await getOrganization(orgId);
        if (org) {
          setOrgName(org.name    || '');
          setTimezone(org.timezone || 'America/New_York');
          setLogoUrl(org.logoUrl  || '');
        }
      } catch (err) {
        console.error('Error loading org:', err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrganization(orgId, { name: orgName, timezone, logoUrl });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving org:', err);
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: '#89A8B1' }}>Loading...</div>;

  return (
    <div style={S.page}>

      {/* Description card */}
      <div style={S.card}>
        <span style={S.section}>Account</span>
        <p style={S.desc}>Manage your organization name, branding, and timezone settings.</p>
      </div>

      {saved && (
        <div style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 8, backgroundColor: '#DCFCE7', color: '#16A34A', fontSize: 14, fontWeight: 600 }}>
          Account settings saved!
        </div>
      )}

      {/* Organization card */}
      <div style={S.card}>
        <span style={S.section}>Organization</span>
        <div style={{ marginBottom: 20 }}>
          <label style={S.label}>Organization Name</label>
          <input
            style={S.input}
            value={orgName}
            onChange={e => setOrgName(e.target.value)}
            placeholder="e.g. Sarah's Realty Group"
          />
        </div>
        <div>
          <label style={S.label}>Logo URL</label>
          <input
            style={S.input}
            value={logoUrl}
            onChange={e => setLogoUrl(e.target.value)}
            placeholder="https://your-logo-url.com/logo.png"
          />
          {logoUrl ? (
            <img src={logoUrl} alt="Logo preview" style={{ marginTop: 12, height: 48, borderRadius: 6, objectFit: 'contain' }} />
          ) : (
            <p style={{ fontSize: 12, color: '#89A8B1', marginTop: 6 }}>Paste a publicly accessible image URL</p>
          )}
        </div>
      </div>

      {/* Timezone card */}
      <div style={S.card}>
        <span style={S.section}>Time Zone</span>
        <select
          style={{ ...S.input, cursor: 'pointer' }}
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
        >
          {TIMEZONES.map(tz => (
            <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', 'Pacific/').replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
          {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// NATIVE MANAGE-ON-WEB MESSAGE (shown in place of billing UI)
// ============================================================
const ManageOnWebMessage = () => (
  <div style={S.page}>
    <div style={S.card}>
      <span style={S.section}>Billing</span>
      <p style={S.desc}>
        Manage your subscription at{' '}
        <a
          href="https://app.getclosd.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#75BB2E', fontWeight: 600, textDecoration: 'none' }}
        >
          app.getclosd.com
        </a>{' '}
        on a web browser.
      </p>
    </div>
  </div>
);

// ============================================================
// BILLING TAB
// ============================================================
export const BillingTab = ({ orgId, user, teamMembers, orgPlan, hideDescription }) => {
  const seatCount    = teamMembers.length || 1;
  const basePrice    = 59;
  const pricePerSeat = 20;
  const additionalSeats = Math.max(0, seatCount - 1);
  const totalMonthly = basePrice + additionalSeats * pricePerSeat;

  const [cancelStep,    setCancelStep]    = useState(null);
  const [cancelReason,  setCancelReason]  = useState('');
  const [cancelNote,    setCancelNote]    = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isComped = orgPlan === 'comped';
  const isSubscribed = orgPlan === 'active' || orgPlan === 'founding';
  const isFounding   = orgPlan === 'founding';
  const isTrial      = orgPlan === 'trial';
  const isCancelled  = orgPlan === 'cancelled';

  const planLabel = isFounding ? 'CLOSd — Founding Member' : isTrial ? 'Free Trial' : isCancelled ? 'Cancelled' : 'CLOSd Subscription';

  const CANCEL_REASONS = ['Too expensive', 'Too hard to use', 'Found a better tool', 'No longer need it', 'Other'];

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      // Pass the org's actual founding status so a prior SuperAdmin grant is
      // preserved through Stripe checkout instead of being overwritten to "active".
      const org = await getOrganization(orgId);
      const isFounding = org?.isFounding100 === true;
      const result = await createCheckoutSession({ orgId, seatCount, isFounding });
      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        alert('Could not start checkout. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const functions = getFunctions();
      const createBillingPortalSession = httpsCallable(functions, 'createBillingPortalSession');
      const result = await createBillingPortalSession({ orgId });
      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        alert('Could not open billing portal. Please try again.');
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSubmitCancel = async () => {
    if (!cancelReason) return;
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const submitCancellation = httpsCallable(functions, 'submitCancellation');
      const result = await submitCancellation({
        orgId, userEmail: user?.email || '',
        reason: cancelReason, note: cancelNote,
      });
      if (!result.data?.success) throw new Error(result.data?.error || 'Unknown error');
      setCancelStep('done');
    } catch (err) {
      console.error('Error saving cancellation:', err);
      alert('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Hard gate — never render billing UI on native (App Store compliance).
  if (isMobileNative) return <ManageOnWebMessage />;

  if (isComped) return (
    <div style={S.page}>
      <div style={S.card}>
        <span style={S.section}>Billing</span>
        <p style={S.desc}>You have complimentary access to CLOSd. No billing required.</p>
      </div>
    </div>
  );

  return (
    <div style={S.page}>

      {/* Description card */}
      {!hideDescription && (
        <div style={S.card}>
          <span style={S.section}>Billing</span>
          <p style={S.desc}>Manage your subscription plan, seats, and payment details.</p>
        </div>
      )}

      {/* Trial — Subscribe CTA */}
      {isTrial && (
        <div style={{ ...S.card, backgroundColor: '#071D39', border: 'none' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#FFFFFF', marginBottom: 8 }}>Ready to subscribe?</div>
          <p style={{ fontSize: 14, color: '#89A8B1', marginBottom: 20, lineHeight: 1.6 }}>
            ${basePrice}/month for 1 seat. Add team members for ${pricePerSeat}/month each, prorated automatically.
          </p>
          <button
            onClick={handleSubscribe}
            disabled={checkoutLoading}
            style={{ padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
          >
            {checkoutLoading ? 'Loading...' : 'Subscribe Now'}
          </button>
        </div>
      )}

      {/* Cancelled notice */}
      {isCancelled && (
        <div style={{ ...S.card, backgroundColor: '#FFF5F5', border: '1px solid #FED7D7' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#E53E3E', marginBottom: 8 }}>Subscription Cancelled</div>
          <p style={{ fontSize: 14, color: '#516469', marginBottom: 16, lineHeight: 1.6 }}>Your subscription has been cancelled. Resubscribe to regain full access.</p>
          <button
            onClick={handleSubscribe}
            disabled={checkoutLoading}
            style={{ padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
          >
            {checkoutLoading ? 'Loading...' : 'Resubscribe'}
          </button>
        </div>
      )}

      {/* Current Plan card — only show when subscribed */}
      {isSubscribed && (
        <div style={S.card}>
          <span style={S.section}>Current Plan</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#071D39' }}>{planLabel}</div>
              {isFounding && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#75BB2E', fontWeight: 600 }}>Founding Member — pricing locked for life 🎉</div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#071D39' }}>${totalMonthly}</div>
              <div style={{ fontSize: 13, color: '#89A8B1' }}>per month</div>
            </div>
          </div>
          <div style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 14, color: '#516469' }}>Base — 1 seat</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#071D39' }}>${basePrice}/mo</span>
            </div>
            {additionalSeats > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, color: '#516469' }}>{additionalSeats} additional seat{additionalSeats !== 1 ? 's' : ''} x ${pricePerSeat}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#071D39' }}>${additionalSeats * pricePerSeat}/mo</span>
              </div>
            )}
            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#071D39' }}>Total</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#071D39' }}>${totalMonthly}/mo</span>
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#89A8B1' }}>
            To update your billing date or invoices, use the <button onClick={handleBillingPortal} style={{ fontSize: 13, color: '#75BB2E', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{portalLoading ? 'Loading...' : 'billing portal'}</button>.
          </div>
        </div>
      )}

      {/* Payment Method card — only show when subscribed */}
      {isSubscribed && (
        <div style={S.card}>
          <span style={S.section}>Payment Method</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#516469' }}>Manage your payment method in the billing portal.</span>
            <button
              onClick={handleBillingPortal}
              disabled={portalLoading}
              style={{ fontSize: 13, color: '#75BB2E', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {portalLoading ? 'Loading...' : 'Update'}
            </button>
          </div>
        </div>
      )}

      {/* Seats card — only show when subscribed */}
      {isSubscribed && (
        <div style={S.card}>
          <span style={S.section}>Seats</span>
          <div style={{ fontSize: 14, color: '#516469', marginBottom: 16 }}>
            You currently have <strong style={{ color: '#071D39' }}>{seatCount} seat{seatCount !== 1 ? 's' : ''}</strong>. Seats are managed automatically when you add or remove team members. Each additional seat is ${pricePerSeat}/month, prorated automatically.
          </div>
        </div>
      )}

      {/* Danger Zone — only show when subscribed */}
      {cancelStep === null && isSubscribed && (
        <div style={{ borderTop: '1px solid #F0F0F0', paddingTop: 32 }}>
          <div style={{ fontSize: 13, color: '#89A8B1', fontWeight: 600, marginBottom: 8 }}>DANGER ZONE</div>
          <button
            onClick={() => setCancelStep('confirm')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E53E3E', fontSize: 14, fontWeight: 600, padding: 0, textDecoration: 'underline' }}
          >
            Cancel Subscription
          </button>
        </div>
      )}

      {/* Cancel Step 1: Confirm */}
      {cancelStep === 'confirm' && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <AlertTriangle size={24} color="#E53E3E" />
              <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39' }}>Cancel your subscription?</div>
            </div>
            <p style={{ fontSize: 14, color: '#516469', lineHeight: 1.6, marginBottom: 24 }}>
              Your account will remain fully active until <strong style={{ color: '#071D39' }}>{'your billing period end date'}</strong>. After that, your account will be deactivated and your data will be retained for 30 days.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setCancelStep(null)} style={{ flex: 1, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}>
                Keep My Account
              </button>
              <button onClick={() => setCancelStep('survey')} style={{ flex: 1, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 600, backgroundColor: 'transparent', color: '#E53E3E', border: '1px solid #E53E3E', cursor: 'pointer' }}>
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Step 2: Survey */}
      {cancelStep === 'survey' && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39', marginBottom: 6 }}>We're sorry to see you go</div>
            <p style={{ fontSize: 14, color: '#516469', marginBottom: 20 }}>Help us improve by telling us why you're leaving:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {CANCEL_REASONS.map(reason => (
                <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${cancelReason === reason ? '#071D39' : '#E2E8F0'}`, backgroundColor: cancelReason === reason ? '#F0F4FF' : '#FFFFFF' }}>
                  <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={() => setCancelReason(reason)} style={{ accentColor: '#071D39' }} />
                  <span style={{ fontSize: 14, color: '#071D39' }}>{reason}</span>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Anything else you'd like us to know? (optional)"
              value={cancelNote}
              onChange={e => setCancelNote(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 14, color: '#071D39', resize: 'none', marginBottom: 20, boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setCancelStep(null)} style={{ flex: 1, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 600, backgroundColor: 'transparent', color: '#516469', border: '1px solid #E2E8F0', cursor: 'pointer' }}>
                Never Mind
              </button>
              <button onClick={handleSubmitCancel} disabled={!cancelReason || submitting} style={{ flex: 1, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: cancelReason ? '#E53E3E' : '#E2E8F0', color: cancelReason ? '#FFFFFF' : '#89A8B1', border: 'none', cursor: cancelReason ? 'pointer' : 'not-allowed' }}>
                {submitting ? 'Cancelling...' : 'Submit & Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Step 3: Done */}
      {cancelStep === 'done' && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <CheckCircle size={48} color="#75BB2E" style={{ marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39', marginBottom: 10 }}>Your subscription has been cancelled</div>
            <p style={{ fontSize: 14, color: '#516469', lineHeight: 1.6, marginBottom: 24 }}>
              You'll have full access to CLOSd until <strong style={{ color: '#071D39' }}>{'your billing period end date'}</strong>. We hope to see you back someday.
            </p>
            <button onClick={() => setCancelStep(null)} style={{ padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700, backgroundColor: '#071D39', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// SETTINGS PAGE SHELL
// ============================================================
const SettingsPage = (props) => {
  // If a deep link / saved state asks for the billing tab on native, redirect to team.
  const safeInitial = (props.initialTab === 'billing' && isMobileNative)
    ? 'team'
    : (props.initialTab || 'team');
  const [activeTab, setActiveTab] = useState(safeInitial);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FFFFFF' }}>

      {/* Header */}
      <div style={{ padding: isMobile ? '16px 16px 0 16px' : '32px 32px 0 32px', borderBottom: '1px solid #E2E8F0' }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#071D39', marginBottom: isMobile ? 16 : 24 }}>Settings</div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: isMobile ? '8px 0' : '10px 20px',
                  flex: isMobile ? 1 : 'none',
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? '#071D39' : '#89A8B1',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: active ? '3px solid #75BB2E' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'profile' && (
          <ProfileTab
            member={props.currentMember}
            isAdmin={props.isAdmin}
            onSave={props.onSave}
          />
        )}
        {activeTab === 'team' && (
          <TeamTab
            teamMembers={props.teamMembers}
            onAddMember={props.onAddMember}
            onRemoveMember={props.onRemoveMember}
            onEditMember={props.onEditMember}
            defaultAssignments={props.defaultAssignments}
            onSaveDefaults={props.onSaveDefaults}
            sellerTemplate={props.sellerTemplate}
            buyerTemplate={props.buyerTemplate}
            isAdmin={props.isAdmin}
            projects={props.projects}
            onRemoveCollaborator={props.onRemoveCollaborator}
          />
        )}
        {activeTab === 'account' && <AccountTab orgId={props.orgId} />}
        {activeTab === 'billing' && (
          isMobileNative ? (
            <ManageOnWebMessage />
          ) : (
            <BillingTab
              orgId={props.orgId}
              user={props.user}
              teamMembers={props.teamMembers}
              orgPlan={props.orgPlan}
            />
          )
        )}
      </div>
    </div>
  );
};

export default SettingsPage;